import * as v from 'valibot';

// Pattern-6 response contract for /account/account-info — the v3 seed for the
// mechanism ported in shared/lib/http/validate-response.ts.
//
// This endpoint was already flagged for it in api.ts ("P1 — pin with a Pattern-6
// contract row"): its JSON is a hand-assembled camelCase map, so several fields
// exist in BOTH casings and the consumers read them as a `??` pair
// (`isPaid ?? is_paid`, `hasZendeskAccount ?? has_zendesk_account`). That is exactly
// the shape a rename slips through unnoticed.
//
// WHAT THIS CAN AND CANNOT CATCH — stated explicitly, because an over-claimed
// contract is worse than none:
//
//   * It CATCHES retypes. Every consumed field below is pinned to its wire type, so
//     a boolean arriving as "true" or a number as "42" reports drift. That is the
//     half that produces the classic silent damage: `remainingTime` as a string
//     feeds a countdown that renders NaN.
//
//   * It does NOT catch a rename of the optional fields. An optional pin is vacuous
//     for renames — `v.object` ignores unknown keys, so the renamed key sails
//     through and the parse succeeds. They stay optional anyway because they are
//     genuinely absent on the wire for some accounts (a free-tier account-info
//     carries no `isPaid` at all — see the MSW default and SupportView's tests), so
//     requiring them would turn the contract into a false-positive generator.
//     Mirroring the consumer's condition does not help here either: the consumers
//     use `??`, so "both absent" is a legitimate state meaning free tier.
//
//   * `state` IS required: it is present on every fixture and real payload we have,
//     and account-info-query dereferences it for the -2 unsubscribe logout. If it
//     ever proves absent for some account shape the failure mode is a telemetry
//     event, not a broken page — the check is observe-only by construction.
//
// `v.object` (not `v.looseObject`) is deliberate: it IGNORES unknown entries rather
// than rejecting them, which is the passthrough behaviour this needs, and the parsed
// output is discarded — only the pass/fail verdict is used.
export const accountInfoSchema = v.object({
  state: v.number(),
  // Paid-tier flag. camelCase is the live contract; snake_case is the fallback the
  // consumers keep for safety. Absent entirely on free tier.
  isPaid: v.optional(v.boolean()),
  is_paid: v.optional(v.boolean()),
  // Set once the account is linked to Zendesk; gates the Support dialogs.
  hasZendeskAccount: v.optional(v.boolean()),
  has_zendesk_account: v.optional(v.boolean()),
  // Trial / reminder countdown pair — both feed arithmetic, so a retype here is the
  // NaN class of bug this contract exists to make loud.
  remainingTime: v.optional(v.number()),
  remindCode: v.optional(v.number()),
  isRootAccount: v.optional(v.boolean()),
});
